import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Buildings, Users, Storefront, ArrowsClockwise, CalendarBlank, Clock, LockSimple, ArrowLeft, Pulse } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <Card className="p-4 bg-card border-border" data-testid={testid}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon className="h-4 w-4" weight="bold" /> {label}</div>
    <p className="text-2xl font-black">{value}</p>
  </Card>
);

export default function OrgPage() {
  const { id } = useParams();
  const { data, isLoading, isError } = useQuery({ queryKey: ["org-page", id], queryFn: async () => (await api.get(`/organizations/${id}/page`)).data, retry: false });

  if (isLoading) return <div className="text-muted-foreground" data-testid="org-page-loading">Loading…</div>;
  if (isError || !data) return <div className="text-muted-foreground" data-testid="org-page-error">Organization not found.</div>;

  const { org, is_member, stats, departments, shifts, swaps, activity } = data;

  return (
    <div className="animate-fade-up max-w-5xl" data-testid="org-page">
      <Link to="/organizations" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 mb-4" data-testid="org-back">
        <ArrowLeft className="h-4 w-4" weight="bold" /> All organizations
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center"><Buildings className="h-7 w-7 text-primary" weight="bold" /></div>
        <div>
          <h1 className="text-3xl font-black tracking-tight">{org.name}</h1>
          <p className="text-muted-foreground text-sm">{org.type}{is_member && <Badge className="bg-emerald-500/15 text-emerald-400 border-0 ml-2 text-[10px] uppercase">Member</Badge>}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <Stat icon={Users} label="Members" value={stats.members} testid="org-stat-members" />
        <Stat icon={Buildings} label="Departments" value={stats.departments} testid="org-stat-departments" />
        <Stat icon={Storefront} label="Open shifts" value={stats.open_shifts} testid="org-stat-shifts" />
        <Stat icon={ArrowsClockwise} label="Open swaps" value={stats.open_swaps} testid="org-stat-swaps" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Departments */}
        <div className="lg:col-span-1">
          <h2 className="font-bold mb-3">Departments & Divisions</h2>
          <div className="space-y-2" data-testid="org-departments">
            {departments.length === 0 && <p className="text-sm text-muted-foreground">No departments listed.</p>}
            {departments.map((d) => (
              <div key={d.name} className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2" data-testid={`org-dept-${d.name}`}>
                <span className="text-sm">{d.name}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" weight="bold" /> {d.members}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Shifts + activity */}
        <div className="lg:col-span-2 space-y-8">
          {!is_member ? (
            <Card className="p-8 text-center bg-card border-border" data-testid="org-locked">
              <LockSimple className="h-8 w-8 text-muted-foreground mx-auto mb-2" weight="bold" />
              <p className="font-semibold">Members only</p>
              <p className="text-sm text-muted-foreground mt-1">Join this organization in your work profile to see open shifts, swaps and activity.</p>
            </Card>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-bold">Open shifts & swaps</h2>
                  <Link to="/marketplace"><Button size="sm" variant="outline" className="rounded-md" data-testid="org-goto-marketplace">Open marketplace</Button></Link>
                </div>
                {(shifts.length + swaps.length) === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="org-no-shifts">Nothing open right now.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="org-shifts">
                    {[...shifts, ...swaps].map((s) => (
                      <Card key={s.id} className="p-4 bg-card border-border" data-testid={`org-shift-${s.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {s.listing_type === "swap" ? <ArrowsClockwise className="h-4 w-4 text-primary" weight="bold" /> : <Storefront className="h-4 w-4 text-primary" weight="bold" />}
                          <span className="font-semibold text-sm">{s.shift_type}</span>
                        </div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><CalendarBlank className="h-3.5 w-3.5" weight="bold" /> {s.date} <Clock className="h-3.5 w-3.5 ml-1" weight="bold" /> {s.start_time}{s.end_time ? `–${s.end_time}` : ""}</p>
                        {s.unit && <p className="text-[11px] text-muted-foreground mt-1">{s.unit}</p>}
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h2 className="font-bold mb-3 flex items-center gap-2"><Pulse className="h-4 w-4 text-primary" weight="bold" /> Recent activity</h2>
                <div className="space-y-2" data-testid="org-activity">
                  {(!activity || activity.length === 0) && <p className="text-sm text-muted-foreground">No recent activity.</p>}
                  {(activity || []).map((a, i) => (
                    <div key={i} className="flex items-center justify-between rounded-md bg-secondary/30 px-3 py-2" data-testid={`org-activity-${i}`}>
                      <span className="text-sm">{a.text}{a.unit ? <span className="text-muted-foreground"> · {a.unit}</span> : ""}</span>
                      <span className="text-[11px] text-muted-foreground shrink-0 ml-2">{a.ts ? formatDistanceToNow(new Date(a.ts), { addSuffix: true }) : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
